import React, { Fragment, useEffect, useState } from 'react';

import { App, Button, Form, Input, Modal, Space, Table, Typography } from 'antd';

import { sleep } from 'lib/Sleep';
import CategoryFilter from 'pages/settings/CategoryFilter';
import {
    createIssueType,
    deleteIssueType,
    listIssueType,
    updateIssueType,
} from 'services/Issue.service';

import { settingsTableColumns } from '../../pages/settings/Columns';

type Category = { id: number; name: string; client_manager_id?: number | null };

export function SettingsIssueType() {
    const app = App.useApp();
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [editing, setEditing] = useState<Category | null>(null);
    const [categories, setCategories] = useState<Category[]>([]);
    const [filtered, setFiltered] = useState<Category[]>([]);

    const fetchCategories = async () => {
        setIsLoading(true);
        const response = await listIssueType();

        if (response.success) {
            setCategories(response.issue_types);
            setFiltered(response.issue_types);
        }

        setIsLoading(false);
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const openModal = (category?: Category) => {
        if (category) {
            form.setFieldsValue({ name: category.name });
            setEditing(category);
        } else {
            form.resetFields();
            setEditing(null);
        }
        setIsModalVisible(true);
    };

    const closeModal = () => {
        form.resetFields();
        setEditing(null);
        setIsModalVisible(false);
    };

    const handleFinish = async (values: { name: string }) => {
        setIsLoading(true);
        if (editing) {
            await handleEdit({ ...editing, name: values.name });
        } else {
            await handleNewCategory(values.name);
        }

        setIsLoading(false);
        closeModal();
    };

    const handleNewCategory = async (name: string) => {
        if (!name.trim()) {
            return app.notification.error({
                message: 'Atenção',
                description: 'Informe o nome da nova categoria',
            });
        }

        const response = await createIssueType({ name });
        await sleep(1000);
        app.notification.open(response);
        await fetchCategories();
    };

    const handleEdit = async (category: Category) => {
        const response = await updateIssueType(category.id, { name: category.name });
        await sleep(1000);
        app.notification.open(response);
        await fetchCategories();
    };

    const handleDelete = (category: Category) => {
        Modal.confirm({
            title: 'Excluir categoria',
            content: (
                <Fragment>
                    Deseja realmente excluir <Typography.Text strong>{category.name}</Typography.Text>?
                </Fragment>
            ),
            okText: 'Sim',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: () => handleDeleteCategory(category.id),
        });
    };

    const handleDeleteCategory = async (id: number) => {
        const response = await deleteIssueType(id);
        await sleep(1000);
        app.notification.open(response);
        await fetchCategories();
    };

    const columns = settingsTableColumns(openModal, handleDelete);

    return (
        <Fragment>
            <Space style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <CategoryFilter
                    onFilterChange={setFiltered}
                    categories={categories}
                />

                <Button type="primary" onClick={() => openModal()}>
                    Cadastrar
                </Button>
            </Space>

            <Table
                style={{ marginTop: 20 }}
                columns={columns}
                dataSource={filtered}
                loading={isLoading}
                pagination={{ pageSize: 5 }}
                rowKey="id"
                bordered
            />

            <Modal
                title={editing ? 'Editar categoria' : 'Nova categoria'}
                open={isModalVisible}
                onCancel={closeModal}
                onOk={() => form.submit()}
                okText="Salvar"
                cancelText="Cancelar"
                confirmLoading={isLoading}
            >
                <Form form={form} layout="vertical" onFinish={handleFinish}>
                    <Form.Item
                        name="name"
                        label="Nome"
                        rules={[{ required: true, message: 'Informe o nome da categoria' }]}
                    >
                        <Input placeholder="Digite o nome da categoria" />
                    </Form.Item>
                </Form>
            </Modal>
        </Fragment>
    );
}
